import { useState } from 'react'
import StateBlock from './StateBlock'
import { isMapped } from '../lib/format'
import type { Geometry, ParkDetail } from '../lib/types'

/** Average of a ring, good enough to hang a number on a pitch. */
function centre(geometry: Geometry): [number, number] {
  if (geometry.type === 'Point') return geometry.coordinates
  const ring = geometry.coordinates[0] ?? []
  if (ring.length === 0) return [0, 0]
  let x = 0
  let y = 0
  for (const [px, py] of ring) {
    x += px
    y += py
  }
  return [x / ring.length, y / ring.length]
}

const points = (ring: [number, number][]) => ring.map(([x, y]) => `${x},${y}`).join(' ')

/**
 * Geometry is stored in image pixel coordinates of the drawing (x right, y down, origin top-left),
 * so it drops straight into a viewBox of "0 0 width height". No flipping, no Leaflet.
 */
export default function PlanPreview({ detail }: { detail: ParkDetail }) {
  const [showNumbers, setShowNumbers] = useState(false)

  const width = detail.map?.width ?? 0
  const height = detail.map?.height ?? 0
  if (!(width > 0 && height > 0)) {
    return <StateBlock kind="empty" message="Nog geen kaartformaat. Vul eerst breedte en hoogte in." />
  }
  if (detail.units.length === 0) {
    return <StateBlock kind="empty" message="Dit park heeft nog geen plaatsen. Importeer de tekening met app:park:import." />
  }

  const scale = Math.max(width, height)
  const stroke = Math.max(1, scale / 700)
  const fontSize = Math.max(8, scale / 90)
  const drawn = detail.units.filter((unit) => unit.geometry)
  // Without a koppeling every plaats would be red, which says nothing; then just draw them plain.
  const hasIntegrations = detail.integrations.length > 0
  const unmapped = hasIntegrations ? detail.units.filter((unit) => !isMapped(unit, detail.integrations)) : []
  const unmappedIds = new Set(unmapped.map((unit) => unit.id))
  const missingOutline = detail.units.length - drawn.length
  // The API resolves the drawing against the public map site; map.image is only a relative fallback.
  const image = detail.mapImageUrl ?? detail.map.image ?? null

  return (
    <>
      {!image && (detail.mapTilesUrl ?? detail.map.tiles) && (
        <p className="muted small">Dit park gebruikt tegels; die worden hier niet geladen. De plaatsen staan wel op hun plek.</p>
      )}
      <div className="preview">
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Plattegrond van ${detail.park.name}`}>
          <rect x={0} y={0} width={width} height={height} fill={detail.map.background ?? '#e9efe4'} />
          {image && <image href={image} x={0} y={0} width={width} height={height} preserveAspectRatio="none" />}
          {detail.zones.map((zone) =>
            zone.geometry && zone.geometry.type === 'Polygon' ? (
              <polygon
                key={zone.id}
                className="plan-zone"
                style={{ strokeWidth: stroke, fill: zone.color ? `${zone.color}33` : undefined, stroke: zone.color ?? undefined }}
                points={points(zone.geometry.coordinates[0] ?? [])}
              >
                <title>{zone.name}</title>
              </polygon>
            ) : null,
          )}
          {detail.units.map((unit) => {
            const geometry = unit.geometry
            if (!geometry) return null
            const bad = unmappedIds.has(unit.id)
            const className = bad ? 'plan-unit unmapped' : 'plan-unit'
            const title = `${unit.number}${unit.name ? ` – ${unit.name}` : ''}${bad ? ' (niet gekoppeld)' : ''}`
            if (geometry.type === 'Point') {
              const [cx, cy] = geometry.coordinates
              return (
                <circle key={unit.id} className={className} style={{ strokeWidth: stroke }} cx={cx} cy={cy} r={scale / 200}>
                  <title>{title}</title>
                </circle>
              )
            }
            return (
              <polygon
                key={unit.id}
                className={className}
                style={{ strokeWidth: bad ? stroke * 2 : stroke }}
                points={points(geometry.coordinates[0] ?? [])}
              >
                <title>{title}</title>
              </polygon>
            )
          })}
          {detail.pois.map((poi) =>
            poi.geometry && poi.geometry.type === 'Point' ? (
              <circle
                key={poi.id}
                className="plan-poi"
                style={{ strokeWidth: stroke }}
                cx={poi.geometry.coordinates[0]}
                cy={poi.geometry.coordinates[1]}
                r={scale / 260}
              >
                <title>{poi.name}</title>
              </circle>
            ) : null,
          )}
          {showNumbers &&
            detail.units.map((unit) =>
              unit.geometry ? (
                <text
                  key={unit.id}
                  x={centre(unit.geometry)[0]}
                  y={centre(unit.geometry)[1] + fontSize / 3}
                  fontSize={fontSize}
                  textAnchor="middle"
                  fill="#1a2521"
                  stroke="#fff"
                  strokeWidth={fontSize / 8}
                  paintOrder="stroke"
                >
                  {unit.number}
                </text>
              ) : null,
            )}
        </svg>
        <div className="legend">
          <span>
            <i style={{ background: 'rgba(30, 92, 67, 0.35)', border: '1px solid #1e5c43' }} />
            {hasIntegrations ? `gekoppeld (${detail.units.length - unmapped.length})` : `${detail.units.length} plaatsen`}
          </span>
          {hasIntegrations && (
            <span>
              <i style={{ background: 'rgba(196, 85, 61, 0.28)', border: '2px solid #c4553d' }} />
              niet gekoppeld ({unmapped.length})
            </span>
          )}
          {!hasIntegrations && <span>Nog geen koppeling, dus geen controle op ontbrekende plaatsen.</span>}
          {missingOutline > 0 && <span className="inline-error">{missingOutline} plaatsen zonder omtrek in de tekening</span>}
          <label className="check" style={{ marginLeft: 'auto' }}>
            <input type="checkbox" checked={showNumbers} onChange={(event) => setShowNumbers(event.target.checked)} />
            plaatsnummers tonen
          </label>
        </div>
      </div>
    </>
  )
}
